import { observer } from 'mobx-react';
import { useEffect } from 'react';
import useHistory from '../../../hooks/useHistory';
import { useStore } from '../../../store/store';
import IndividualDetailsHeader from './IndividualDetailsHeader';
import IndividualRelationsTable from './IndividualRelationsTable';
import ContactInformationSection from './ContactInformationSection';

/**
 * IndividualDetails - Main component for displaying individual profile details
 *
 * Shows person information header and their company relations table.
 * Data is passed in as props (loaded by parent component).
 *
 * @param {Object} individual - Individual data from API
 * @param {boolean} hideBackButton - Whether to hide back button
 * @param {string} backButtonLabel - Label for back button (e.g., "Back to Denseo GmbH")
 * @param {Object} project - Project context for watchlist operations
 */
const IndividualDetails = observer(
  ({ individual = {}, hideBackButton = false, backButtonLabel = null, project }) => {
    const { navigate } = useHistory();
    const { profilerAccessStore } = useStore();

    useEffect(() => {
      document.title = `transact.digital GmbH - ${individual.fullName || individual.full_name || 'Individual details'}`;
    }, [individual.fullName, individual.full_name]);

    return (
      <div className="individual-details-container">
        {/* Back button - uses browser history for navigation */}
        {!hideBackButton && (
          <button onClick={() => navigate(-1)} className="backlink">
            {backButtonLabel || 'Back'}
          </button>
        )}

        {/* Header section with person info */}
        <IndividualDetailsHeader individual={individual} />

        {/* Contact information (reveal) — gated on the Cognito enrichment group.
            Rendered above the relations table per TD-2079 AC16-18. */}
        {profilerAccessStore.isCognitoContactEnrichment && (
          <ContactInformationSection individual={individual} />
        )}

        {/* Company relations table */}
        <IndividualRelationsTable
          individual={individual}
          companies={individual.companies || []}
          project={project}
        />
      </div>
    );
  }
);

export default IndividualDetails;
